import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';

const TABS = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
  { id: 'cancelled', label: 'Cancelled' },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const TicketCutout = ({ children }) => (
  <div className="relative">
    <div className="absolute w-5 h-5 bg-[#F9FAFB] rounded-full -left-2.5 bottom-24 z-10" />
    <div className="absolute w-5 h-5 bg-[#F9FAFB] rounded-full -right-2.5 bottom-24 z-10" />
    {children}
  </div>
);

const TicketCard = ({ booking }) => {
  const [isReminderSet, setIsReminderSet] = useState(false);
  const ticketId = `#BY-${booking.id.slice(0, 6).toUpperCase()}`;
  const seatSection = booking.items[0]?.tier_name ? `${booking.items[0].tier_name}` : 'General Entry';

  return (
    <TicketCutout>
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100" data-testid="ticket-card">
        <div className="p-5 bg-[#2B3A67] text-white flex justify-between items-start">
          <div>
            <div className="bg-[#3AAFA9]/20 text-[#3AAFA9] text-[10px] font-black px-2 py-0.5 rounded border border-[#3AAFA9]/30 inline-block mb-2 uppercase tracking-widest">
              VIP PASS
            </div>
            <h2 className="text-xl font-bold leading-tight" data-testid="ticket-event-title">
              {booking.event_title}
            </h2>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">Ticket ID</p>
            <p className="text-xs font-mono font-bold" data-testid="ticket-id">{ticketId}</p>
          </div>
        </div>

        <div className="p-5 grid grid-cols-2 gap-4 border-b border-dashed border-gray-200">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Date & Time</p>
            <p className="text-sm font-bold" data-testid="ticket-date">{new Date(booking.event_date).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Location</p>
            <p className="text-sm font-bold truncate" data-testid="ticket-location">{booking.venue_name}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Seat/Section</p>
            <p className="text-sm font-bold" data-testid="ticket-seat">{seatSection}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Order Total</p>
            <p className="text-sm font-bold" data-testid="ticket-total">{formatCurrency(booking.total_amount)}</p>
          </div>
        </div>

        <div className="p-6 flex flex-col items-center justify-center bg-gray-50/50">
          <div className="bg-white p-3 rounded-2xl shadow-sm mb-4 border border-gray-100">
            <div className="w-32 h-32 bg-[#2B3A67] flex items-center justify-center rounded-lg">
              <div className="grid grid-cols-3 gap-1 p-2 bg-white rounded">
                {Array.from({ length: 9 }).map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-6 h-6 ${idx === 4 ? 'border-2 border-[#2B3A67]' : 'bg-[#2B3A67]'}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]" data-testid="ticket-qr-label">
            Scan at Entrance
          </p>
        </div>

        <div className="px-5 pb-6 flex gap-3">
          <Link
            to={`/bookings/${booking.id}`}
            className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-[#2B3A67] text-[#2B3A67] font-bold py-3 rounded-xl text-sm"
            data-testid="ticket-view-button"
          >
            <i className="ph-bold ph-share-network"></i> View
          </Link>
          <button
            onClick={() => setIsReminderSet(!isReminderSet)}
            className={`flex-1 flex items-center justify-center gap-2 font-bold py-3 rounded-xl text-sm ${
              isReminderSet ? 'bg-[#2B3A67] text-white' : 'bg-[#3AAFA9] text-white'
            }`}
            data-testid="ticket-reminder-button"
          >
            <i className="ph-fill ph-bell-ringing"></i>
            {isReminderSet ? 'Reminder Set' : 'Remind Me'}
          </button>
        </div>
      </div>
    </TicketCutout>
  );
};

const CompactTicketCard = ({ booking }) => {
  const eventDate = new Date(booking.event_date);
  const month = eventDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const day = eventDate.getDate();

  return (
    <div className="bg-white rounded-2xl p-4 flex gap-4 shadow-md border border-gray-50" data-testid="compact-ticket-card">
      <div className="w-20 h-20 bg-[#E54562] rounded-xl flex flex-col items-center justify-center text-white shrink-0">
        <span className="text-xs font-bold uppercase">{month}</span>
        <span className="text-2xl font-black">{day}</span>
      </div>
      <div className="flex-1">
        <h4 className="font-bold text-[#2B3A67] leading-tight mb-1" data-testid="compact-ticket-title">
          {booking.event_title}
        </h4>
        <p className="text-xs text-gray-500 flex items-center gap-1 mb-3" data-testid="compact-ticket-location">
          <i className="ph-fill ph-map-pin text-[#E54562]"></i> {booking.venue_name}
        </p>
        <div className="flex gap-2">
          <Link
            to={`/bookings/${booking.id}`}
            className="text-[10px] font-black uppercase tracking-wider text-[#3AAFA9] border border-[#3AAFA9] px-2 py-1 rounded"
            data-testid="compact-ticket-view"
          >
            View Ticket
          </Link>
          <button
            className="text-[10px] font-black uppercase tracking-wider text-gray-400 border border-gray-200 px-2 py-1 rounded"
            data-testid="compact-ticket-resell"
          >
            Resell
          </button>
        </div>
      </div>
    </div>
  );
};

const SafetyReminder = () => (
  <div className="bg-[#F4D341]/10 border-2 border-[#F4D341] rounded-2xl p-5 flex items-start gap-4" data-testid="ticket-safety-reminder">
    <div className="w-10 h-10 bg-[#F4D341] rounded-full flex items-center justify-center shrink-0 shadow-sm">
      <i className="ph-fill ph-warning-circle text-[#2B3A67] text-xl"></i>
    </div>
    <div>
      <h4 className="font-bold text-[#2B3A67] text-sm">Safety Reminder</h4>
      <p className="text-xs text-[#2B3A67]/70 leading-relaxed mt-1">
        Only buy tickets from verified sellers. Tickets transferred outside of Backyard are not guaranteed.
      </p>
    </div>
  </div>
);

// Empty states share the profile's dashed-card style.
const EmptyTickets = ({ icon, title, body, cta, onCta, testId }) => (
  <div
    className="text-center py-10 px-6 bg-white rounded-2xl border-2 border-dashed border-gray-200"
    data-testid={testId}
  >
    <div className="w-14 h-14 mx-auto rounded-full bg-[#F9FAFB] flex items-center justify-center shadow-sm">
      <i className={`ph-bold ${icon} text-[#E54562] text-2xl`} />
    </div>
    <p className="mt-4 font-extrabold text-[#2B3A67]">{title}</p>
    {body && <p className="mt-1 text-sm text-gray-500 leading-relaxed">{body}</p>}
    {cta && (
      <button
        onClick={onCta}
        className="mt-5 px-5 py-2.5 rounded-full bg-[#2B3A67] text-white text-sm font-bold"
        data-testid="tickets-browse-button"
      >
        {cta}
      </button>
    )}
  </div>
);

export default function TicketsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBookings = async () => {
      try {
        const response = await axiosInstance.get('/bookings');
        setBookings(response.data);
      } catch (error) {
        toast.error('Unable to load tickets');
      } finally {
        setLoading(false);
      }
    };

    loadBookings();
  }, []);

  const now = new Date();
  const upcoming = bookings.filter((booking) => new Date(booking.event_date) >= now);
  const past = bookings.filter((booking) => new Date(booking.event_date) < now);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center pb-nav">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]"></div>
      </div>
    );
  }

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-nav text-[#2B3A67]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-30 bg-[#F9FAFB]/90 backdrop-blur-md border-b border-gray-100 pt-safe" data-testid="tickets-header">
        <div className="px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/profile')}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100"
            aria-label="Back to profile"
            data-testid="tickets-back-button"
          >
            <i className="ph-bold ph-arrow-left text-xl" />
          </button>
          <div>
            <h1 className="font-extrabold text-xl">My Tickets</h1>
            <p className="text-xs text-gray-400">Your event passes and bookings.</p>
          </div>
        </div>

        {/* Same switch style as the Market's Shop / Services toggle */}
        <div className="px-6 pb-3">
          <div className="bg-white rounded-2xl border border-gray-100 p-1 flex" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-colors ${
                  activeTab === tab.id ? 'bg-[#2B3A67] text-white' : 'text-gray-500'
                }`}
                data-testid={`tickets-tab-${tab.id}`}
              >
                {tab.label}
                {tab.id === 'upcoming' && upcoming.length > 0 ? ` (${upcoming.length})` : ''}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="px-6 pt-6 space-y-6">
        {activeTab === 'upcoming' && (
          <>
            {upcoming.length === 0 ? (
              <EmptyTickets
                testId="tickets-empty-state"
                icon="ph-ticket"
                title="No upcoming tickets"
                body="Browse events and grab your spot. Your tickets will show up here."
                cta="Browse events"
                onCta={() => navigate('/events')}
              />
            ) : (
              <>
                <TicketCard booking={upcoming[0]} />
                {upcoming.length > 1 && (
                  <div className="space-y-4" data-testid="tickets-other-upcoming">
                    <h3 className="font-bold text-lg">Other Upcoming</h3>
                    <CompactTicketCard booking={upcoming[1]} />
                  </div>
                )}
                <SafetyReminder />
              </>
            )}
          </>
        )}

        {activeTab === 'past' && (
          <>
            {past.length === 0 ? (
              <EmptyTickets testId="tickets-past-empty" icon="ph-clock-counter-clockwise" title="No past tickets" body="Events you've attended will appear here." />
            ) : (
              past.map((booking) => <CompactTicketCard key={booking.id} booking={booking} />)
            )}
          </>
        )}

        {activeTab === 'cancelled' && (
          <EmptyTickets testId="tickets-cancelled-empty" icon="ph-x-circle" title="No cancelled tickets" />
        )}
      </main>
    </div>
  );
}